import account from '../constants/analytics/categoryActionLabel/account';
import mobileDownload from '../constants/analytics/categoryActionLabel/mobileDownload';
import subscribe from '../constants/analytics/categoryActionLabel/subscribe';
import unsubscribe from '../constants/analytics/categoryActionLabel/unsubscribe';
import mobileWeb from '../constants/analytics/categoryActionLabel/mobileWeb';
import web from '../constants/analytics/categoryActionLabel/web';
import play from '../constants/analytics/categoryActionLabel/play';
import coupon from '../constants/analytics/categoryActionLabel/coupon';
import feature from '../constants/analytics/categoryActionLabel/feature';
import isServer from '../utils/isServer';
import { isLocalEnv } from '../utils/environment';

export const LOG_CATEGORY_ACTION_LABEL = 'LOG_CATEGORY_ACTION_LABEL';
export const LOG_CLIENT_ERROR = 'LOG_CLIENT_ERROR';
export const LOG_CLIENT_INFO = 'LOG_CLIENT_INFO';
export const LOG_INFO = 'LOG_INFO';

export function createEventString(event) {
  return [event.category, event.action, event.label, event.value]
    .join('|')
    .replace(/\|+$/, ''); // removes any trailing `|`
}

export function createEventBody(eventString) {
  return `event1=${eventString}`;
}

export function createEventParams(moreParams = {}) {
  return {
    c: 'eventlist',
    render: 'json',
    ...moreParams,
  };
}

export function logClientError(errorData = {}) {
  return (dispatch) => {
    if (!errorData.message || typeof errorData.context !== 'object') {
      throw new Error(
        'The `message` and `context` attributes must be defined.',
      );
    }

    if (isServer()) {
      return null;
    }

    const { error = {} } = errorData.context;

    if (error instanceof Error) {
      // needed because `stack` isn't an enumerable property and will get dropped when stringified to JSON
      errorData.context.error = { ...error, stack: error.stack }; // eslint-disable-line no-param-reassign
    }

    if (isLocalEnv()) {
      // eslint-disable-next-line no-console
      console.error(errorData.message, errorData.context);
    }

    return dispatch({
      type: LOG_CLIENT_ERROR,
      api: {
        endpoint: ['logging', 'errorLog'],
        args: [errorData],
      },
      meta: errorData,
    });
  };
}

export function logClientInfo(data = {}) {
  return (dispatch) => {
    if (!data.message || !data.context) {
      throw new Error(
        'The `message` and `context` attributes must be defined.',
      );
    }

    if (isServer()) {
      return null;
    }

    return dispatch({
      type: LOG_CLIENT_INFO,
      api: {
        endpoint: ['logging', 'infoLog'],
        args: [data],
      },
      meta: data,
    });
  };
}

export function logCategoryActionLabel(event, moreParams = {}) {
  return (dispatch) => {
    const params = createEventParams(moreParams);
    const eventString =
      typeof event === 'string' ? event : createEventString(event);
    const body = createEventBody(eventString);

    return dispatch({
      type: LOG_CATEGORY_ACTION_LABEL,
      api: {
        endpoint: ['logging', 'eventLog'],
        args: [params, body],
      },
    }).catch((error) =>
      dispatch(
        logClientError({
          message: `EVENT LOGGING FAILURE: ${error.message}`,
          context: {
            event: eventString,
            params: moreParams,
            error,
          },
        }),
      ),
    );
  };
}

export function logFeatureActivity(action, label) {
  const event = {
    category: feature.category,
    action,
    label,
  };
  return logCategoryActionLabel(event);
}

export function logMobileDownloadClick(newLabel, guideId) {
  const event = {
    category: mobileDownload.category,
    action: mobileDownload.action,
    label: newLabel,
  };

  return (dispatch) => dispatch(logCategoryActionLabel(event, { id: guideId }));
}

export function logWebActivity(newAction, newLabel, guideId) {
  const event = {
    category: web.category,
    action: newAction,
    label: newLabel,
  };

  return (dispatch) => dispatch(logCategoryActionLabel(event, { id: guideId }));
}

export function logSubscribeActivity(newAction, newLabel, guideId) {
  const event = {
    category: subscribe.category,
    action: newAction,
    label: guideId ? `${newLabel}.${guideId}` : newLabel,
  };

  return (dispatch) => dispatch(logCategoryActionLabel(event, { id: guideId }));
}

export function logUnsubscribeActivity(newAction, newLabel, guideId) {
  const event = {
    category: unsubscribe.category,
    action: newAction,
    label: newLabel,
  };

  return (dispatch) => dispatch(logCategoryActionLabel(event, { id: guideId }));
}

export function logMobileWebActivity(newAction, newLabel) {
  const event = {
    category: mobileWeb.category,
    action: newAction,
    label: newLabel,
  };

  return (dispatch) => dispatch(logCategoryActionLabel(event));
}

export function logAccountActivity(action, labelArray) {
  const label = labelArray.filter(Boolean).join('.');
  const event = {
    category: account.category,
    action,
    label,
  };

  return (dispatch) => dispatch(logCategoryActionLabel(event));
}

export function logSignInClick(label, source) {
  return logAccountActivity(account.actions.click, [
    account.labels.signIn,
    label,
    source,
  ]);
}

export function logSignUpClick(label, source) {
  return logAccountActivity(account.actions.click, [
    account.labels.signUp,
    label,
    source,
  ]);
}

export function logSignInDismiss() {
  return logAccountActivity(account.actions.dismiss, [account.labels.signIn]);
}

export function logSignUpDismiss() {
  return logAccountActivity(account.actions.dismiss, [account.labels.signUp]);
}

export function logPartnerAuthConfirmationShow(source) {
  return logAccountActivity(account.actions.show, [
    account.labels.confirmation,
    account.labels.partner,
    source,
  ]);
}

export function logPartnerAuthConfirmationAllowClick(source, productSku) {
  return logAccountActivity(account.actions.allow, [
    account.labels.confirmation,
    productSku,
    account.labels.partner,
    source,
  ]);
}

export function logPartnerAuthConfirmationDenyClick(source) {
  return logAccountActivity(account.actions.deny, [
    account.labels.confirmation,
    account.labels.partner,
    source,
  ]);
}

export function logPlayStart({ guideId: id, isAutoplayed = false, itemToken }) {
  const event = {
    category: play.category,
    action: play.actions.start,
    label: isAutoplayed ? play.labels.auto : play.labels.manual,
  };

  return (dispatch) =>
    dispatch(logCategoryActionLabel(event, { id, itemToken }));
}

export function logPrerollEligible({ audioEnabled, videoEnabled }) {
  const event = {
    category: play.category,
    action: play.actions.start,
    label: `${play.labels.preroll}.video.${videoEnabled}.audio.${audioEnabled}`,
  };

  return (dispatch) => dispatch(logCategoryActionLabel(event));
}

function logTuneTimeReport(
  action,
  id,
  time,
  playerNameList,
  itemToken,
  hadPreroll,
  hasAdBlocker,
) {
  const { preroll, noPreroll, adBlockerEnabled } = play.labels;
  const prerollLabelSuffix = hadPreroll ? preroll : noPreroll;
  const finalLabelSuffix = hasAdBlocker ? adBlockerEnabled : prerollLabelSuffix;
  const event = {
    category: play.category,
    action,
    label: [playerNameList?.join('.'), finalLabelSuffix].join('.'),
    value: time,
  };

  return (dispatch) =>
    dispatch(logCategoryActionLabel(event, { id, itemToken }));
}

export function logStreamEvaluationSuccessTime({
  guideId,
  time,
  playerNameList,
  itemToken,
  hadPreroll,
}) {
  return logTuneTimeReport(
    play.actions.successMs,
    guideId,
    time,
    playerNameList,
    itemToken,
    hadPreroll,
  );
}

export function logStreamEvaluationFailTime({
  guideId,
  time,
  playerNameList,
  itemToken,
  hadPreroll,
}) {
  return logTuneTimeReport(
    play.actions.failMs,
    guideId,
    time,
    playerNameList,
    itemToken,
    hadPreroll,
  );
}

export function logTotalTuneSuccessTime({
  guideId,
  time,
  playerNameList,
  itemToken,
  hadPreroll,
}) {
  return logTuneTimeReport(
    play.actions.totalSuccessMs,
    guideId,
    time,
    playerNameList,
    itemToken,
    hadPreroll,
  );
}

export function logTotalTuneFailTime({
  guideId,
  time,
  playerNameList,
  itemToken,
  hadPreroll,
  hasAdBlocker,
}) {
  return logTuneTimeReport(
    play.actions.totalFailMs,
    guideId,
    time,
    playerNameList,
    itemToken,
    hadPreroll,
    hasAdBlocker,
  );
}

export function logPlayStartNext({ guideId: id, itemToken }) {
  const event = {
    category: play.category,
    action: play.actions.start,
    label: play.labels.next,
  };

  return (dispatch) =>
    dispatch(logCategoryActionLabel(event, { id, itemToken }));
}

export function logInfo(...args) {
  return {
    type: LOG_INFO,
    data: args,
  };
}

export function logCouponActivity(couponAction, campaign, couponCode) {
  const event = {
    category: coupon.category,
    action: couponAction,
    label: [campaign, couponCode].filter(Boolean).join('.'),
  };

  return logCategoryActionLabel(event);
}

export function logMessageOfTheDayAction(label, value) {
  const event = {
    category: feature.category,
    action: feature.actions.messageOfTheDay,
    label: value ? `${label}.${value}` : label,
  };

  return logCategoryActionLabel(event);
}

export function logInterestSelectorAction(label, value) {
  const event = {
    category: feature.category,
    action: feature.actions.interestSelector,
    label: value ? `${label}.${value}` : label,
  };

  return logCategoryActionLabel(event);
}

export function logLinkAlexaAction(label) {
  const event = {
    category: feature.category,
    action: feature.actions.linkAlexa,
    label,
  };

  return (dispatch) => dispatch(logCategoryActionLabel(event));
}
